import React from "react";
import {
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Autocomplete,
  FormHelperText,
  FormControlLabel,
  Switch,
  Typography,
  Box,
} from "@mui/material";
import InputMask from "react-input-mask";
import PhoneField from "../PhoneField";

export default function ClientFormFieldControl({
  field,
  label,
  formData,
  errors,
  onChange,
  onCepChange,
  onCompanyChange,
  companies,
  companiesLoading,
  clientGroups,
  groupsLoading,
  tagOptions,
  onTagsChange,
  onTagSearch,
  onGrupoChange,
  size = "small",
}) {
  const commonTextField = (extra = {}) => (
    <TextField
      label={label}
      name={field.name}
      value={formData[field.name] ?? ""}
      onChange={onChange}
      type={field.type}
      size={size}
      InputLabelProps={field.type === "date" ? { shrink: true } : undefined}
      fullWidth
      disabled={!!field.disabled || !!extra.disabled}
      error={!!errors[field.name]}
      helperText={errors[field.name]}
      {...extra}
    />
  );

  if (field.type === "boolean") {
    const checked = Boolean(Number(formData[field.name]));
    return (
      <FormControlLabel
        control={
          <Switch
            name={field.name}
            checked={checked}
            onChange={(e) =>
              onChange({
                target: {
                  name: field.name,
                  value: e.target.checked ? 1 : 0,
                },
              })
            }
            color="primary"
          />
        }
        label={
          <Box>
            <Typography variant="body2" fontWeight={600}>
              {label}
            </Typography>
            {field.helperText ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                {field.helperText}
              </Typography>
            ) : null}
          </Box>
        }
        sx={{ alignItems: "flex-start", ml: 0, mr: 0 }}
      />
    );
  }

  if (field.type === "grupo") {
    return (
      <FormControl fullWidth variant="outlined" size={size} error={!!errors[field.name]}>
        <InputLabel id={`${field.name}-label`}>{label}</InputLabel>
        <Select
          labelId={`${field.name}-label`}
          name={field.name}
          value={formData.id_grupo ?? ""}
          onChange={onGrupoChange}
          label={label}
          disabled={groupsLoading}
        >
          <MenuItem value="">
            <em>Nenhum</em>
          </MenuItem>
          {clientGroups.map((g) => (
            <MenuItem key={g.id} value={g.id}>
              {g.nome || `Grupo ${g.id}`}
            </MenuItem>
          ))}
        </Select>
        {errors[field.name] ? <FormHelperText>{errors[field.name]}</FormHelperText> : null}
      </FormControl>
    );
  }

  if (field.type === "select") {
    if (field.name === "id_projeto") {
      return (
        <Autocomplete
          size={size}
          options={companies}
          getOptionLabel={(o) => o?.nome || o?.name || ""}
          value={companies.find((c) => String(c.id) === String(formData.id_projeto)) || null}
          onChange={onCompanyChange}
          loading={companiesLoading}
          loadingText="Carregando empresas..."
          isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
          renderInput={(params) => (
            <TextField {...params} label="Empresa" placeholder="Selecione a empresa" fullWidth size={size} />
          )}
        />
      );
    }
    return (
      <FormControl fullWidth variant="outlined" size={size} error={!!errors[field.name]}>
        <InputLabel>{label}</InputLabel>
        <Select name={field.name} value={formData[field.name] ?? ""} onChange={onChange} label={label}>
          {(field.options || []).map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
        {errors[field.name] ? <FormHelperText>{errors[field.name]}</FormHelperText> : null}
      </FormControl>
    );
  }

  if (field.type === "textarea") {
    return commonTextField({ multiline: true, rows: 3 });
  }

  if (field.type === "tags") {
    return (
      <Autocomplete
        multiple
        size={size}
        filterSelectedOptions
        options={(tagOptions || [])
          .filter((t) => (t?.status == null ? true : Number(t.status) === 1))
          .map((t) => t?.nome)
          .filter(Boolean)}
        value={Array.isArray(formData.tags) ? formData.tags : []}
        onChange={(_e, next) => onTagsChange(next)}
        onInputChange={(_e, v) => onTagSearch(v)}
        renderInput={(params) => (
          <TextField {...params} label="Tags" placeholder="Selecione tags" fullWidth size={size} />
        )}
      />
    );
  }

  if (field.name === "cep") {
    return (
      <InputMask mask="99999-999" value={formData[field.name] ?? ""} onChange={onCepChange} maskChar={null}>
        {(inputProps) => (
          <TextField
            {...inputProps}
            label="CEP"
            size={size}
            fullWidth
            error={!!errors[field.name]}
            helperText={errors[field.name]}
          />
        )}
      </InputMask>
    );
  }

  if (field.name === "cpf") {
    return (
      <InputMask mask="999.999.999-99" value={formData[field.name] ?? ""} onChange={onChange} maskChar={null}>
        {(inputProps) => (
          <TextField
            {...inputProps}
            label={label}
            name={field.name}
            size={size}
            fullWidth
            error={!!errors[field.name]}
            helperText={errors[field.name]}
          />
        )}
      </InputMask>
    );
  }

  if (field.name === "cnpj") {
    return (
      <InputMask mask="99.999.999/9999-99" value={formData[field.name] ?? ""} onChange={onChange} maskChar={null}>
        {(inputProps) => (
          <TextField
            {...inputProps}
            label={label}
            name={field.name}
            size={size}
            fullWidth
            error={!!errors[field.name]}
            helperText={errors[field.name]}
          />
        )}
      </InputMask>
    );
  }

  if (field.name === "telefone" || field.name === "celular" || field.name === "whatsapp") {
    return (
      <PhoneField
        name={field.name}
        label={label}
        value={formData[field.name] ?? ""}
        onChange={onChange}
        error={!!errors[field.name]}
        helperText={errors[field.name]}
        required={!!field.required}
      />
    );
  }

  return commonTextField();
}
